import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  Button,
  Chip,
  Divider,
  Drawer,
  FormControl,
  FormLabel,
  IconButton,
  Radio,
  Stack,
  Typography,
} from '@mui/joy';
import { BiLock } from 'react-icons/bi';
import { FiPlusCircle } from 'react-icons/fi';
import { IoMenu } from 'react-icons/io5';
import { Link } from 'react-router-dom';

import {
  FOLLOWING_PROJECTS_ON_SEARCH_PAGE,
  NavItem,
  ProfileMenuItem,
} from './hooks/useNavbarState';

// Re-use styled components from parent — passed as props to avoid circular deps
interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  navItemsConfig: NavItem[];
  profileMenuConfig: ProfileMenuItem[];
  themeOptionsList: Array<{ label: string; value: 'light' | 'dark' }>;
  envOptionsList: Array<{ label: string; value: boolean }>;
  mode: string | undefined;
  isCalyxTeamMember: boolean;
  appIsStaging: boolean;
  filterState: any;
  followingProjectsCount: number | undefined;
  isLoading: boolean;
  locationPathname: string;
  handleNavItemClick: (event: string) => void;
  handleThemeSwitch: (mode: 'dark' | 'light') => void;
  handleEnvToggle: () => void;
  handleFollowingClick: () => void;
  logout: () => void;
  StyledNavBox: any;
  StyledAccordionSummary: any;
  StyledLink: any;
  analyticsTrack: (event: string, page: string) => void;
  signoutEvent: string;
  dashboardPage: string;
}

export const MobileDrawer = ({
  isOpen,
  onClose,
  navItemsConfig,
  profileMenuConfig,
  themeOptionsList,
  envOptionsList,
  mode,
  isCalyxTeamMember,
  appIsStaging,
  filterState,
  followingProjectsCount,
  isLoading,
  locationPathname,
  handleNavItemClick,
  handleThemeSwitch,
  handleEnvToggle,
  handleFollowingClick,
  logout,
  StyledNavBox,
  StyledAccordionSummary,
  StyledLink,
  analyticsTrack,
  signoutEvent,
  dashboardPage,
}: MobileDrawerProps) => {
  return (
    <Drawer open={isOpen} anchor='right' sx={{ display: { lg: 'none' } }} onClose={onClose}>
      <Stack direction={'row'} justifyContent={'end'} sx={{ m: 1 }}>
        <IconButton variant='plain' onClick={onClose}>
          <IoMenu fontSize={20} />
        </IconButton>
      </Stack>
      {navItemsConfig.map(
        ({
          path,
          title,
          subtitle,
          isNew,
          isDropdown = false,
          items = [],
          isBeta,
          event,
          show,
          activeCallback,
          hasPermission = true,
        }) => {
          if (!show) return null;
          // Longest match wins, so a topic page does not also highlight the hub entry above it.
          const activeChildPath = items
            .map((item) => item.path)
            .filter((itemPath) => locationPathname.startsWith(itemPath))
            .sort((a, b) => b.length - a.length)[0];

          return isDropdown ? (
            <Accordion variant='plain' key={path}>
              <AccordionGroup disableDivider sx={{ maxWidth: 400 }}>
                <StyledAccordionSummary sx={{ px: 3, py: 1 }}>{title}</StyledAccordionSummary>
                <AccordionDetails>
                  {items.map(
                    ({
                      path,
                      title,
                      event: childEvent,
                      isNew,
                      isBeta,
                      isSubItem,
                      hasPermission: childHasPermission = true,
                    }) => (
                      <Link
                        key={path}
                        to={path}
                        style={{
                          textDecoration: 'none',
                          ...(isSubItem && { display: 'block', paddingLeft: 24 }),
                        }}
                        onClick={() => handleNavItemClick(childEvent)}
                      >
                        <StyledNavBox active={path === activeChildPath}>
                          <Stack gap={1}>
                            <Typography
                              fontSize={'md'}
                              color='primary'
                              startDecorator={childHasPermission ? null : <BiLock />}
                              endDecorator={
                                isNew || isBeta ? (
                                  <>
                                    {isNew ? (
                                      <Chip variant='solid' color='primary'>
                                        New
                                      </Chip>
                                    ) : null}
                                    {isBeta ? (
                                      <Chip variant='solid' color='primary'>
                                        Beta
                                      </Chip>
                                    ) : null}
                                  </>
                                ) : null
                              }
                            >
                              {title}
                            </Typography>
                            {subtitle && (
                              <Typography
                                fontSize={'sm'}
                                fontWeight={'md'}
                                sx={{ color: (theme) => theme.palette.text.secondary }}
                              >
                                {subtitle}
                              </Typography>
                            )}
                          </Stack>
                        </StyledNavBox>
                      </Link>
                    )
                  )}
                </AccordionDetails>
              </AccordionGroup>
            </Accordion>
          ) : (
            <Link
              key={path}
              to={path}
              style={{ textDecoration: 'none' }}
              onClick={() => handleNavItemClick(event)}
            >
              <StyledNavBox
                active={!!locationPathname.startsWith(path) || activeCallback?.(locationPathname)}
              >
                <Typography
                  startDecorator={hasPermission ? null : <BiLock />}
                  endDecorator={
                    isNew || isBeta ? (
                      <>
                        {isNew ? (
                          <Chip variant='solid' color='primary'>
                            New
                          </Chip>
                        ) : null}
                        {isBeta ? (
                          <Chip variant='solid' color='primary'>
                            Beta
                          </Chip>
                        ) : null}
                      </>
                    ) : null
                  }
                >
                  {title}
                </Typography>
              </StyledNavBox>
            </Link>
          );
        }
      )}
      {/* account menu mobile view */}
      <Stack px={3} py={1}>
        <Divider />
        <FormControl sx={{ my: 1 }}>
          <FormLabel sx={{ fontWeight: 'xl', fontSize: 'sm' }}>Appearance</FormLabel>
          {themeOptionsList.map(({ label, value }) => (
            <Stack
              key={value}
              direction={'row'}
              gap={1}
              alignItems={'center'}
              onClick={() => handleThemeSwitch(value)}
            >
              <Radio
                value={value}
                variant='outlined'
                size='md'
                color='primary'
                sx={{ fontSize: 'md' }}
                checked={mode === value}
              />
              <Typography fontSize={'md'} fontWeight={'sm'}>
                {label}
              </Typography>
            </Stack>
          ))}
        </FormControl>
        {isCalyxTeamMember ? (
          <FormControl sx={{ my: 1 }}>
            <FormLabel sx={{ fontWeight: 'xl', fontSize: 'sm' }}>App Environment</FormLabel>
            {envOptionsList.map(({ label, value }) => (
              <Stack
                key={label}
                onClick={handleEnvToggle}
                direction={'row'}
                gap={1}
                alignItems={'center'}
              >
                <Radio
                  value={value}
                  variant='outlined'
                  size='md'
                  color='primary'
                  sx={{ fontSize: 'md' }}
                  checked={appIsStaging === value}
                />
                <Typography fontSize={'md'} fontWeight={'sm'}>
                  {label}
                </Typography>
              </Stack>
            ))}
          </FormControl>
        ) : null}
        <Divider />
        <Stack direction={'row'} py={1} onClick={handleFollowingClick}>
          <StyledLink
            to={`${FOLLOWING_PROJECTS_ON_SEARCH_PAGE}${filterState?.searchParams.has('view') ? `&view=${filterState?.searchParams.get('view')}` : ''}`}
            sx={{ height: (theme) => theme.spacing(4) }}
          >
            <Button
              size='md'
              variant='plain'
              color='primary'
              startDecorator={<FiPlusCircle size={20} />}
              sx={{ padding: 0 }}
            >
              <Typography fontSize={'md'} fontWeight={'sm'}>
                Following ({isLoading ? '...' : followingProjectsCount})
              </Typography>
            </Button>
          </StyledLink>
        </Stack>
        {profileMenuConfig.map(({ onClick, title, show }) => {
          if (!show || title.toLowerCase() === 'log out') return null;
          return (
            <div key={title}>
              <Divider />
              <Stack direction={'row'} py={1} onClick={onClick}>
                <Typography fontSize={'md'} fontWeight={'sm'}>
                  {title}
                </Typography>
              </Stack>
            </div>
          );
        })}
        <Button
          variant='outlined'
          onClick={() => {
            analyticsTrack(signoutEvent, dashboardPage);
            logout();
          }}
          sx={{ mt: 2 }}
        >
          Log out
        </Button>
      </Stack>
    </Drawer>
  );
};
